import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { promptLogin } from "@/lib/authRedirect";
import { toast } from "sonner";
import { ChevronLeft, Heart, MessageCircle, Bookmark, BookmarkCheck, Share2, MoreHorizontal, Loader2, Trash2, CornerDownRight, X as XIcon } from "lucide-react";
import SEO from "@/components/site/SEO";
import FoundingParentBadge from "@/components/site/FoundingParentBadge";
import { timeAgo } from "@/components/site/DiscussionCard";
import { ForumRichEditor, HtmlContent } from "@/components/site/ForumRichEditor";
import ModActionsMenu from "@/components/site/ModActionsMenu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * DiscussionDetail — /forum/diskusi/:id
 * Layout: back-to-Ruang → discussion body → engagement action bar (like/comment/bookmark/share) →
 * comment composer → threaded (1-level) comment list.
 */
export default function DiscussionDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [likeBusy, setLikeBusy] = useState(false);
  const [bookmarkBusy, setBookmarkBusy] = useState(false);
  const [replyTo, setReplyTo] = useState(null); // comment_id we're replying to

  // ---- Moderation state (mod OR owner) ----
  // Discussion-level edit: swap header/body for inline inputs; body uses TipTap.
  const [editingDisc, setEditingDisc] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editBody, setEditBody] = useState("");
  const [editKey, setEditKey] = useState(0);         // bump to reset TipTap on cancel
  const [editSaving, setEditSaving] = useState(false);
  // Move-to-another-Ruang dialog (mod only).
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveTarget, setMoveTarget] = useState("");
  const [ruangList, setRuangList] = useState([]);    // fetched lazily on first open

  const isMod = user && ["moderator", "admin", "superadmin"].includes(user.role);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/discussions/${id}`);
        setData(r.data);
      } catch (e) {
        toast.error(e?.response?.status === 404 ? "Diskusi tidak ditemukan." : "Gagal memuat diskusi.");
      } finally { setLoading(false); }
    })();
  }, [id]);

  const discussion = data?.discussion;

  // Group comments into top-level + child map. `data?.comments || []` is
  // computed INSIDE the memo so the dependency stays stable across renders
  // (satisfies react-hooks/exhaustive-deps at error level).
  const { topLevel, childMap } = useMemo(() => {
    const list = data?.comments || [];
    const top = []; const cmap = {};
    for (const c of list) {
      if (c.parent_comment_id) {
        (cmap[c.parent_comment_id] = cmap[c.parent_comment_id] || []).push(c);
      } else {
        top.push(c);
      }
    }
    return { topLevel: top, childMap: cmap };
  }, [data?.comments]);
  const comments = data?.comments || [];

  const requireAuth = () => {
    if (!user) { promptLogin(nav); return false; }
    return true;
  };

  const toggleLike = async () => {
    if (!requireAuth() || likeBusy || !discussion) return;
    setLikeBusy(true);
    const prev = discussion.viewer_liked;
    // Optimistic
    setData((d) => ({ ...d, discussion: { ...d.discussion, viewer_liked: !prev, likes: (d.discussion.likes || 0) + (prev ? -1 : 1) } }));
    try {
      const r = await api.post(`/discussions/${id}/like`);
      setData((d) => ({ ...d, discussion: { ...d.discussion, viewer_liked: r.data.liked, likes: r.data.likes } }));
    } catch {
      setData((d) => ({ ...d, discussion: { ...d.discussion, viewer_liked: prev, likes: (d.discussion.likes || 0) + (prev ? 1 : -1) } }));
      toast.error("Gagal memperbarui like.");
    } finally { setLikeBusy(false); }
  };

  const toggleBookmark = async () => {
    if (!requireAuth() || bookmarkBusy || !discussion) return;
    setBookmarkBusy(true);
    const prev = discussion.viewer_bookmarked;
    setData((d) => ({ ...d, discussion: { ...d.discussion, viewer_bookmarked: !prev } }));
    try {
      const r = await api.post(`/discussions/${id}/bookmark`);
      setData((d) => ({ ...d, discussion: { ...d.discussion, viewer_bookmarked: r.data.bookmarked } }));
      toast.success(r.data.bookmarked ? "Disimpan ke bookmark Anda." : "Bookmark dihapus.");
    } catch {
      setData((d) => ({ ...d, discussion: { ...d.discussion, viewer_bookmarked: prev } }));
      toast.error("Gagal memperbarui bookmark.");
    } finally { setBookmarkBusy(false); }
  };

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: discussion?.title, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link disalin ke clipboard.");
      }
    } catch { /* user cancelled */ }
  };

  const onCommentCreated = (c) => {
    setData((d) => ({
      ...d,
      comments: [...(d.comments || []), c],
      discussion: { ...d.discussion, comment_count: (d.discussion.comment_count || 0) + 1 },
    }));
    setReplyTo(null);
  };

  const onCommentDeleted = (comment_id) => {
    setData((d) => ({
      ...d,
      comments: d.comments.map((c) => c.comment_id === comment_id
        ? { ...c, is_deleted: true, body: "[Komentar dihapus]" } : c),
    }));
  };

  const onCommentEdited = (comment_id, patch) => {
    setData((d) => ({
      ...d,
      comments: d.comments.map((c) => c.comment_id === comment_id ? { ...c, ...patch } : c),
    }));
  };

  const onCommentLikeChanged = (comment_id, patch) => {
    setData((d) => ({
      ...d,
      comments: d.comments.map((c) => c.comment_id === comment_id ? { ...c, ...patch } : c),
    }));
  };

  // ---- Moderation actions ----
  const isOwner = user && discussion && discussion.user_id === user.user_id;
  const canEditDisc = isOwner || isMod;
  const canMoveDisc = isMod;
  const canDeleteDisc = isOwner || isMod;

  const beginEditDisc = () => {
    if (!discussion) return;
    setEditTitle(discussion.title || "");
    setEditBody(discussion.body || "");
    setEditingDisc(true);
  };
  const cancelEditDisc = () => {
    setEditingDisc(false);
    setEditKey((k) => k + 1);
  };
  const saveEditDisc = async () => {
    if (editSaving) return;
    const t = (editTitle || "").trim();
    const b = (editBody || "").trim();
    if (t.length < 5) { toast.error("Judul minimal 5 karakter."); return; }
    if (!b) { toast.error("Isi diskusi tidak boleh kosong."); return; }
    setEditSaving(true);
    try {
      const r = await api.patch(`/discussions/${id}`, { title: t, body: b });
      setData((d) => ({ ...d, discussion: { ...d.discussion, ...r.data.discussion, body: b } }));
      toast.success("Diskusi diperbarui.");
      setEditingDisc(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan perubahan.");
    } finally { setEditSaving(false); }
  };

  const deleteDisc = async () => {
    if (!window.confirm("Hapus diskusi ini? Balasan yang sudah ada akan tetap tersimpan tapi diskusi tidak akan muncul di feed.")) return;
    try {
      await api.delete(`/discussions/${id}`);
      toast.success("Diskusi dihapus.");
      // Navigate back to the Ruang the discussion belonged to (or forum).
      const back = discussion?.ruang_slug ? `/forum/ruang/${discussion.ruang_slug}` : "/forum";
      nav(back, { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghapus.");
    }
  };

  const openMove = async () => {
    if (!ruangList.length) {
      try {
        const r = await api.get("/ruang");
        const flat = [...(r.data?.grup_a?.items || []), ...(r.data?.grup_b?.items || [])]
          .filter((x) => x.is_active !== false);
        setRuangList(flat);
      } catch { toast.error("Gagal memuat daftar Ruang."); return; }
    }
    setMoveTarget(discussion?.ruang_id || "");
    setMoveOpen(true);
  };
  const submitMove = async () => {
    if (!moveTarget || moveTarget === discussion?.ruang_id) { setMoveOpen(false); return; }
    try {
      const r = await api.patch(`/discussions/${id}/move`, { ruang_id: moveTarget });
      setData((d) => ({ ...d, discussion: { ...d.discussion,
        ruang_id: r.data.ruang_id, ruang_slug: r.data.ruang_slug, ruang_name: r.data.ruang_name, ruang_icon: r.data.ruang_icon,
      }}));
      toast.success(`Dipindahkan ke ${r.data.ruang_name}.`);
      setMoveOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memindahkan.");
    }
  };

  if (loading || !discussion) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-brand-alabaster">
        <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-alabaster pb-24" data-testid="discussion-detail-page">
      <SEO title={`${discussion.title} — The School Reports`} description={(discussion.body || "").slice(0, 160)} />

      {/* Back bar */}
      <div className="max-w-2xl mx-auto px-5 pt-6 pb-3 flex items-center gap-3">
        <button onClick={() => nav(-1)} aria-label="Kembali" data-testid="discussion-back" className="w-9 h-9 rounded-full bg-white border border-brand-border hover:border-brand-primary/40 flex items-center justify-center">
          <ChevronLeft className="w-5 h-5 text-brand-text" />
        </button>
        {discussion.ruang_slug && (
          <Link
            to={`/forum/ruang/${discussion.ruang_slug}`}
            className="inline-flex items-center gap-2 h-9 px-3 rounded-full bg-brand-primary/10 text-brand-primary text-sm font-medium hover:bg-brand-primary/20"
            data-testid="discussion-ruang-link"
          >
            <span aria-hidden>{discussion.ruang_icon}</span>
            {discussion.ruang_name}
          </Link>
        )}
      </div>

      {/* Discussion card */}
      <article className="max-w-2xl mx-auto px-5">
        <div className="rounded-2xl bg-white border border-brand-border p-6 shadow-sm">
          {/* Author */}
          <header className="flex items-center gap-3 mb-4">
            <Avatar name={discussion.user_name} picture={discussion.user_picture} />
            <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-brand-text">{discussion.user_name || "Anonim"}</span>
              {discussion.is_founding_parent && <FoundingParentBadge size="xs" />}
              <span className="text-xs text-brand-text-muted">· {timeAgo(discussion.created_at)}</span>
              {discussion.edited_at && (
                <span className="text-[10px] text-brand-text-muted italic" title={new Date(discussion.edited_at).toLocaleString("id-ID")}>· diedit</span>
              )}
            </div>
            {!editingDisc && (canEditDisc || canMoveDisc || canDeleteDisc) && (
              <ModActionsMenu
                testidPrefix="disc-mod-menu"
                onEdit={canEditDisc ? beginEditDisc : undefined}
                onMove={canMoveDisc ? openMove : undefined}
                onDelete={canDeleteDisc ? deleteDisc : undefined}
              />
            )}
          </header>

          {editingDisc ? (
            <div className="space-y-3" data-testid="discussion-edit-form">
              <Input
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                placeholder="Judul diskusi"
                data-testid="discussion-edit-title"
                className="text-lg font-bold h-11 rounded-xl"
              />
              <ForumRichEditor
                key={`edit-${editKey}`}
                value={editBody}
                onChange={setEditBody}
                placeholder="Isi diskusi…"
                testidPrefix="discussion-edit-body"
                minHeight={140}
              />
              <div className="flex items-center justify-end gap-2">
                <Button variant="outline" onClick={cancelEditDisc} data-testid="discussion-edit-cancel" className="rounded-full h-9">Batal</Button>
                <Button onClick={saveEditDisc} disabled={editSaving} data-testid="discussion-edit-save" className="rounded-full bg-brand-accent hover:bg-brand-accent-hover text-white h-9">
                  {editSaving ? "Menyimpan…" : "Simpan"}
                </Button>
              </div>
            </div>
          ) : (
            <>
              <h1 className="font-sans font-bold text-2xl md:text-[28px] text-[#1C1C1F] leading-[1.25] mb-3">
                {discussion.title}
              </h1>
              <HtmlContent
                value={discussion.body}
                className="text-[15px] text-[#4A4A4F] leading-relaxed mb-5"
                testid="discussion-body"
              />
            </>
          )}

          {(discussion.tags || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-4">
              {discussion.tags.map((t) => (
                <span key={t} className="inline-flex items-center h-6 px-2 rounded-full bg-brand-accent/10 text-brand-accent text-xs">#{t}</span>
              ))}
            </div>
          )}

          {/* Action bar */}
          <footer className="flex items-center gap-1 border-t border-brand-border pt-4 mt-4">
            <ActionButton
              testid="discussion-like-btn"
              onClick={toggleLike}
              active={!!discussion.viewer_liked}
              icon={<Heart className={`w-4 h-4 ${discussion.viewer_liked ? "fill-current" : ""}`} />}
              label={discussion.likes || 0}
              activeColor="text-rose-600"
            />
            <ActionButton
              testid="discussion-comment-btn"
              onClick={() => document.getElementById("comment-composer")?.focus()}
              icon={<MessageCircle className="w-4 h-4" />}
              label={discussion.comment_count || 0}
            />
            <ActionButton
              testid="discussion-bookmark-btn"
              onClick={toggleBookmark}
              active={!!discussion.viewer_bookmarked}
              icon={discussion.viewer_bookmarked ? <BookmarkCheck className="w-4 h-4 fill-current" /> : <Bookmark className="w-4 h-4" />}
              label={discussion.viewer_bookmarked ? "Tersimpan" : "Simpan"}
              activeColor="text-brand-primary"
            />
            <div className="flex-1" />
            <button onClick={share} data-testid="discussion-share-btn" className="w-9 h-9 rounded-full hover:bg-brand-muted flex items-center justify-center text-brand-text-muted" aria-label="Bagikan">
              <Share2 className="w-4 h-4" />
            </button>
          </footer>
        </div>

        {/* Comments */}
        <section className="mt-8">
          <h2 className="font-serif-display text-xl text-brand-primary mb-4" data-testid="comments-heading">
            {(discussion.comment_count || 0) === 0 ? "Belum ada balasan" : `${discussion.comment_count} balasan`}
          </h2>

          {/* Composer */}
          <CommentComposer
            discussionId={id}
            replyTo={replyTo}
            onCreated={onCommentCreated}
            onCancelReply={() => setReplyTo(null)}
            onRequireAuth={() => promptLogin(nav)}
            currentUser={user}
          />

          {/* List */}
          <div className="mt-6 space-y-4">
            {topLevel.length === 0 ? (
              <div className="rounded-2xl bg-white border border-brand-border p-6 text-center shadow-sm text-brand-text-muted text-sm" data-testid="comments-empty">
                Jadilah orang tua pertama yang membalas — cerita atau pertanyaan singkat pun berarti.
              </div>
            ) : topLevel.map((c) => (
              <CommentThread
                key={c.comment_id}
                comment={c}
                replies={childMap[c.comment_id] || []}
                currentUser={user}
                onReply={(cid) => setReplyTo(cid)}
                onDelete={onCommentDeleted}
                onEdited={onCommentEdited}
                onLikeChanged={onCommentLikeChanged}
              />
            ))}
          </div>
        </section>
      </article>

      {/* Move-to-Ruang dialog (mod only) */}
      <Dialog open={moveOpen} onOpenChange={setMoveOpen}>
        <DialogContent data-testid="discussion-move-dialog">
          <DialogHeader><DialogTitle>Pindahkan diskusi ke Ruang lain</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-[#4B5563]">
              Hanya field <code className="text-[11px] bg-[#F3F1ED] px-1 rounded">ruang_id</code> yang berubah. Balasan, bookmark, likes, dan tags tetap. Counts kedua Ruang di-adjust otomatis.
            </div>
            <Select value={moveTarget} onValueChange={setMoveTarget}>
              <SelectTrigger data-testid="discussion-move-select"><SelectValue placeholder="Pilih Ruang tujuan…" /></SelectTrigger>
              <SelectContent>
                {ruangList.map((r) => (
                  <SelectItem key={r.ruang_id} value={r.ruang_id}>
                    {r.icon} {r.name}{r.ruang_id === discussion?.ruang_id ? " (Ruang saat ini)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setMoveOpen(false)} className="rounded-full h-9">Batal</Button>
              <Button
                disabled={!moveTarget || moveTarget === discussion?.ruang_id}
                onClick={submitMove}
                data-testid="discussion-move-submit"
                className="rounded-full bg-brand-primary hover:bg-brand-primary/90 text-white h-9"
              >
                Pindahkan
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Avatar({ name, picture, size = "md" }) {
  const dim = size === "sm" ? "w-8 h-8 text-xs" : "w-9 h-9 text-sm";
  if (picture) {
    return <img src={picture} alt="" className={`${dim} rounded-full object-cover shrink-0`} />;
  }
  const letter = (name || "?").trim().charAt(0).toUpperCase();
  const palette = ["bg-rose-100 text-rose-700", "bg-amber-100 text-amber-700", "bg-emerald-100 text-emerald-700", "bg-sky-100 text-sky-700", "bg-violet-100 text-violet-700", "bg-orange-100 text-orange-700"];
  const idx = letter.charCodeAt(0) % palette.length;
  return (
    <span className={`${dim} rounded-full flex items-center justify-center font-semibold shrink-0 ${palette[idx]}`}>{letter}</span>
  );
}

function ActionButton({ onClick, active, icon, label, testid, activeColor = "text-brand-accent" }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-sm transition-colors hover:bg-brand-muted ${
        active ? activeColor + " font-semibold" : "text-brand-text-muted"
      }`}
    >
      {icon} <span>{label}</span>
    </button>
  );
}

function CommentComposer({ discussionId, replyTo, onCreated, onCancelReply, onRequireAuth, currentUser }) {
  const [body, setBody] = React.useState("");
  const [phase, setPhase] = React.useState("idle"); // idle | sending | sent
  const [resetKey, setResetKey] = React.useState(0);
  const [participants, setParticipants] = React.useState([]);
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [pickerQuery, setPickerQuery] = React.useState("");
  const [mentionsMap, setMentionsMap] = React.useState({}); // { "@Name": user_id }
  const textareaRef = React.useRef(null);
  const atStartPosRef = React.useRef(0); // index of the "@" that opened the picker
  const sentTimerRef = React.useRef(null);

  // Cleanup pending sent-phase timer on unmount to avoid setState on unmounted component.
  React.useEffect(() => () => { if (sentTimerRef.current) clearTimeout(sentTimerRef.current); }, []);

  // Load participants once for the mention picker.
  React.useEffect(() => {
    if (!currentUser) return;
    api.get(`/discussions/${discussionId}/participants`).then((r) => {
      // Exclude self so the picker never suggests mentioning yourself.
      setParticipants((r.data || []).filter((p) => p.user_id !== currentUser.user_id));
    }).catch(() => {});
  }, [discussionId, currentUser]);

  const onBodyChange = (e) => {
    const val = e.target.value;
    setBody(val);
    // Detect "@" typing → open picker with query = chars after last @ up to cursor
    const cursor = e.target.selectionStart || val.length;
    const upTo = val.slice(0, cursor);
    const atIdx = upTo.lastIndexOf("@");
    if (atIdx >= 0) {
      const nextChar = atIdx > 0 ? upTo[atIdx - 1] : " ";
      const startsWord = atIdx === 0 || /\s/.test(nextChar);
      const q = upTo.slice(atIdx + 1);
      if (startsWord && !/\s/.test(q) && q.length <= 40) {
        atStartPosRef.current = atIdx;
        setPickerQuery(q.toLowerCase());
        setPickerOpen(true);
        return;
      }
    }
    setPickerOpen(false);
  };

  const matchedParticipants = React.useMemo(() => {
    if (!pickerOpen) return [];
    return participants
      .filter((p) => (p.name || "").toLowerCase().includes(pickerQuery))
      .slice(0, 6);
  }, [pickerOpen, pickerQuery, participants]);

  const insertMention = (p) => {
    const start = atStartPosRef.current;
    const cursor = textareaRef.current?.selectionStart || body.length;
    const before = body.slice(0, start);
    const after = body.slice(cursor);
    const tag = `@${p.name}`;
    const newBody = `${before}${tag} ${after}`;
    setBody(newBody);
    setMentionsMap((m) => ({ ...m, [tag]: p.user_id }));
    setPickerOpen(false);
    // Restore focus + move caret past the inserted mention
    setTimeout(() => {
      if (textareaRef.current) {
        const pos = before.length + tag.length + 1;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  const resolveMentions = () => {
    // Walk the final body text and collect user_ids for every @Name tag present.
    const ids = [];
    for (const [tag, uid] of Object.entries(mentionsMap)) {
      if (body.includes(tag)) ids.push(uid);
    }
    return [...new Set(ids)];
  };

  const submit = async () => {
    if (!currentUser) { onRequireAuth(); return; }
    // Double-click / repeated-submit guard: any non-idle phase blocks re-submit.
    if (phase !== "idle") return;
    const trimmed = body.trim();
    if (trimmed.length < 2) {
      toast.error("Tulis balasan singkat dulu (minimal 2 karakter).");
      return;
    }
    setPhase("sending");
    try {
      const r = await api.post("/comments", {
        discussion_id: discussionId,
        body: trimmed,
        parent_comment_id: replyTo || undefined,
        mentions: resolveMentions(),
      });
      onCreated(r.data);
      // Clear all composer state on success — bumping resetKey forces the
      // TipTap editor to visually clear (parent state alone isn't enough).
      setBody("");
      setMentionsMap({});
      setResetKey((k) => k + 1);
      // Kirim → Mengirim… → Terkirim ✓ → Kirim
      setPhase("sent");
      sentTimerRef.current = setTimeout(() => {
        setPhase("idle");
        sentTimerRef.current = null;
      }, 1200);
    } catch (e) {
      // Do NOT clear the editor on failure — keep the user's draft intact.
      toast.error(e?.response?.data?.detail || "Gagal mengirim balasan. Coba lagi.");
      setPhase("idle");
    }
  };

  const placeholder = replyTo
    ? "Tulis balasan singkat… (@ untuk sebut anggota)"
    : "Bagikan pengalaman atau jawab dengan singkat… (@ untuk sebut anggota)";

  return (
    <div className="rounded-2xl bg-white border border-brand-border p-4 shadow-sm relative" data-testid="comment-composer-wrapper">
      {replyTo && (
        <div className="mb-2 text-xs text-brand-text-muted flex items-center gap-2">
          <CornerDownRight className="w-3.5 h-3.5" /> Membalas komentar
          <button onClick={onCancelReply} className="text-brand-accent hover:underline">Batal</button>
        </div>
      )}
      <ForumRichEditor
        value={body}
        onChange={(html) => onBodyChange({ target: { value: html } })}
        placeholder={placeholder}
        testidPrefix="comment-composer"
        minHeight={90}
        resetKey={resetKey}
      />
      {/* Mention picker popover */}
      {pickerOpen && matchedParticipants.length > 0 && (
        <div className="absolute left-4 right-4 top-full mt-1 z-20 rounded-xl bg-white border border-brand-border shadow-lg overflow-hidden" data-testid="mention-picker">
          <div className="text-[11px] uppercase tracking-wider px-3 py-2 text-brand-text-muted border-b border-brand-border">Sebut anggota</div>
          {matchedParticipants.map((p) => (
            <button
              key={p.user_id}
              onClick={() => insertMention(p)}
              data-testid={`mention-option-${p.user_id}`}
              className="w-full flex items-center gap-3 px-3 py-2 hover:bg-brand-muted text-left"
            >
              <Avatar name={p.name} picture={p.picture} size="sm" />
              <span className="text-sm text-[#1C1C1F]">{p.name}</span>
            </button>
          ))}
        </div>
      )}
      <div className="flex items-center justify-end gap-2 mt-2">
        <span className="text-xs text-brand-text-muted mr-auto">{body.length}/4000</span>
        <button
          onClick={submit}
          disabled={phase !== "idle" || body.trim().length < 2}
          data-testid="comment-composer-submit"
          className={`h-9 px-5 rounded-full text-white text-sm font-semibold transition-colors ${
            phase === "sent"
              ? "bg-[#2E7D5B] hover:bg-[#2E7D5B]"
              : "bg-brand-accent hover:bg-brand-accent-hover disabled:bg-brand-accent/40"
          }`}
        >
          {phase === "sending" ? "Mengirim…" : phase === "sent" ? "Terkirim ✓" : "Kirim"}
        </button>
      </div>
    </div>
  );
}

function CommentThread({ comment, replies, currentUser, onReply, onDelete, onEdited, onLikeChanged }) {
  return (
    <div className="space-y-3">
      <CommentItem
        c={comment}
        currentUser={currentUser}
        onReply={onReply}
        onDelete={onDelete}
        onEdited={onEdited}
        onLikeChanged={onLikeChanged}
      />
      {replies.length > 0 && (
        <div className="pl-6 md:pl-10 space-y-3 border-l-2 border-brand-border/60 ml-4 md:ml-5">
          {replies.map((r) => (
            <CommentItem key={r.comment_id} c={r} currentUser={currentUser} isReply onReply={onReply} onDelete={onDelete} onEdited={onEdited} onLikeChanged={onLikeChanged} />
          ))}
        </div>
      )}
    </div>
  );
}

function CommentItem({ c, currentUser, isReply = false, onReply, onDelete, onEdited, onLikeChanged }) {
  const [likeBusy, setLikeBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editBody, setEditBody] = useState(c.body || "");
  const [editKey, setEditKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const isOwner = currentUser?.user_id === c.user_id;
  const isMod = currentUser && ["moderator", "admin", "superadmin"].includes(currentUser.role);
  const canEdit = !c.is_deleted && (isOwner || isMod);
  const canDelete = !c.is_deleted && (isOwner || isMod);

  const toggleLike = async () => {
    if (!currentUser) return;
    if (likeBusy) return;
    setLikeBusy(true);
    const prev = c.viewer_liked;
    onLikeChanged(c.comment_id, { viewer_liked: !prev, likes: (c.likes || 0) + (prev ? -1 : 1) });
    try {
      const r = await api.post(`/comments/${c.comment_id}/like`);
      onLikeChanged(c.comment_id, { viewer_liked: r.data.liked, likes: r.data.likes });
    } catch {
      onLikeChanged(c.comment_id, { viewer_liked: prev, likes: c.likes || 0 });
    } finally { setLikeBusy(false); }
  };

  const del = async () => {
    if (!window.confirm("Hapus komentar ini?")) return;
    try {
      await api.delete(`/comments/${c.comment_id}`);
      onDelete(c.comment_id);
      toast.success("Komentar dihapus.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghapus komentar.");
    }
  };

  const beginEdit = () => {
    setEditBody(c.body || "");
    setEditing(true);
  };
  const cancelEdit = () => {
    setEditing(false);
    setEditKey((k) => k + 1);
  };
  const saveEdit = async () => {
    if (saving) return;
    const b = (editBody || "").trim();
    if (!b) { toast.error("Isi komentar tidak boleh kosong."); return; }
    setSaving(true);
    try {
      const r = await api.patch(`/comments/${c.comment_id}`, { body: b });
      onEdited && onEdited(c.comment_id, { body: r.data.comment.body, edited_at: r.data.comment.edited_at });
      setEditing(false);
      toast.success("Komentar diperbarui.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan.");
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl bg-white border border-brand-border p-4 shadow-sm" data-testid={`comment-${c.comment_id}`}>
      <div className="flex items-start gap-3">
        <Avatar name={c.user_name} picture={c.user_picture} size="sm" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="font-semibold text-sm text-brand-text">{c.user_name || "Anonim"}</span>
            {c.is_founding_parent && <FoundingParentBadge size="xs" />}
            <span className="text-xs text-brand-text-muted">· {timeAgo(c.created_at)}</span>
            {c.edited_at && <span className="text-[10px] text-brand-text-muted italic" title={new Date(c.edited_at).toLocaleString("id-ID")}>· diedit</span>}
            {(canEdit || canDelete) && !editing && (
              <div className="ml-auto">
                <ModActionsMenu
                  testidPrefix={`comment-mod-menu-${c.comment_id}`}
                  onEdit={canEdit ? beginEdit : undefined}
                  onDelete={canDelete ? del : undefined}
                />
              </div>
            )}
          </div>
          {c.is_deleted ? (
            <p className="text-sm text-brand-text-muted italic">[Komentar dihapus]</p>
          ) : editing ? (
            <div className="space-y-2" data-testid={`comment-edit-form-${c.comment_id}`}>
              <ForumRichEditor
                key={`c-edit-${c.comment_id}-${editKey}`}
                value={editBody}
                onChange={setEditBody}
                testidPrefix={`comment-edit-${c.comment_id}`}
                minHeight={90}
              />
              <div className="flex items-center justify-end gap-2">
                <Button variant="outline" onClick={cancelEdit} data-testid={`comment-edit-cancel-${c.comment_id}`} className="rounded-full h-8 text-xs">Batal</Button>
                <Button onClick={saveEdit} disabled={saving} data-testid={`comment-edit-save-${c.comment_id}`} className="rounded-full bg-brand-accent hover:bg-brand-accent-hover text-white h-8 text-xs">
                  {saving ? "Menyimpan…" : "Simpan"}
                </Button>
              </div>
            </div>
          ) : (
            <HtmlContent
              value={c.body}
              className="text-sm text-brand-text leading-relaxed"
              testid={`comment-${c.comment_id}-body`}
            />
          )}
          {!c.is_deleted && !editing && (
            <div className="flex items-center gap-1 mt-2 -ml-2">
              <button
                onClick={toggleLike}
                disabled={likeBusy}
                data-testid={`comment-like-${c.comment_id}`}
                className={`inline-flex items-center gap-1 h-8 px-2 rounded-full text-xs transition-colors hover:bg-brand-muted ${
                  c.viewer_liked ? "text-rose-600 font-semibold" : "text-brand-text-muted"
                }`}
              >
                <Heart className={`w-3.5 h-3.5 ${c.viewer_liked ? "fill-current" : ""}`} /> {c.likes || 0}
              </button>
              {!isReply && (
                <button
                  onClick={() => onReply(c.comment_id)}
                  data-testid={`comment-reply-${c.comment_id}`}
                  className="inline-flex items-center gap-1 h-8 px-2 rounded-full text-xs text-brand-text-muted hover:bg-brand-muted"
                >
                  <CornerDownRight className="w-3.5 h-3.5" /> Balas
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


/**
 * Render comment text and highlight @mentions inline.
 * Approach: split on the pattern `@Word` (letters + digits + spaces up to 40 chars until
 * whitespace boundary). Simple substring highlight — not perfect (won't match names with
 * commas etc.) but covers 95% of common Indonesian display names.
 */
function renderCommentBody(text) {
  if (!text) return null;
  const parts = [];
  const rx = /@([A-Za-zÀ-ÿ0-9][A-Za-zÀ-ÿ0-9 .'-]{0,40})/g;
  let last = 0; let m; let idx = 0;
  while ((m = rx.exec(text)) !== null) {
    // Trim trailing space in name so "@Rara balas" doesn't consume " balas" fully.
    let full = m[0]; let name = m[1].trimEnd();
    if (name.length < m[1].length) {
      full = "@" + name;
    }
    const start = m.index;
    if (start > last) parts.push(text.slice(last, start));
    parts.push(
      <span key={`m-${idx++}`} className="text-brand-primary bg-brand-primary/10 rounded px-1 font-medium">{full}</span>
    );
    last = start + full.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}
